import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, HistoryIcon, MoreHorizontalIcon, MoreVerticalIcon } from 'lucide-react';
import { MenuButton } from '../ui/MenuButton';
import { PillButton } from '../ui/PillButton';
import { HistoryEntryRow } from './HistoryEntryRow';
import { SavedVersionsList } from './SavedVersionsList';
import { groupByDay } from '../../utils/historyText';
import type { HistoryEntry, SavedVersion } from '../../types/history';
type Tab = 'timeline' | 'versions';
type HistoryPanelProps = {
  entries: HistoryEntry[];
  versions: SavedVersion[];
  partName: string;
  onClose: () => void;
  onRequestRestore: (entry: HistoryEntry) => void;
  onSaveVersion: (entry: HistoryEntry) => void;
  onRestoreVersion: (version: SavedVersion) => void;
};
export function HistoryPanel({
  entries,
  versions,
  partName,
  onClose,
  onRequestRestore,
  onSaveVersion,
  onRestoreVersion
}: HistoryPanelProps) {
  const [tab, setTab] = useState<Tab>('timeline');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = entries.find(e => e.id === selectedId) ?? entries[0] ?? null;
  const currentId = entries[0]?.id;
  const groups = groupByDay(entries);
  return <div className="flex h-full w-[340px] flex-col bg-white" aria-label="Project history" data-id="element-211" data-mp="1q5w0r8njjx3j">
      <div className="px-[12px] pt-[10px]" data-id="element-212" data-mp="1l82vqixz594u">
        <div className="flex items-start gap-[10px]" data-id="element-213" data-mp="xhqeuikr4224">
          <button type="button" aria-label="Close history" onClick={onClose} className="mt-[2px] flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[6px] border border-pf-line bg-white text-pf-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-colors duration-150 hover:bg-pf-panel" data-id="element-214" data-mp="xii99q2m3tqt">
            
            <ChevronLeftIcon className="h-[15px] w-[15px]" strokeWidth={2.4} data-id="element-215" data-mp="2iujsnn2spkq2" />
          </button>
          <div className="min-w-0 flex-1" data-id="element-216" data-mp="2dx1g7mpkkndb">
            <h2 className="text-[17px] font-bold leading-tight text-pf-ink" data-id="element-217" data-mp="2dvyrnlfpyx0k">History</h2>
            <p className="truncate text-[11px] text-pf-muted" title={partName} data-id="element-218" data-mp="2kywu29fglrti">
              Changes to <span className="font-semibold text-pf-ink" data-id="element-219" data-mp="2px83ij1ue0b7">{partName}</span> · select another model to switch
            </p>
          </div>
          <MenuButton label="History options" icon={<MoreVerticalIcon className="h-[15px] w-[15px]" strokeWidth={2.4} data-id="element-221" data-mp="2zw755ho6wvop" />} items={[{
          label: 'Save current state as version',
          onSelect: () => entries[0] && onSaveVersion(entries[0]),
          disabled: !entries[0]
        }, {
          label: 'Close history',
          onSelect: onClose
        }]} data-id="element-220" data-mp="2uvrvb034a6bh" />
          
        </div>
        <div role="tablist" aria-label="History view" className="mt-[10px] grid grid-cols-2 rounded-[6px] border border-pf-line bg-white p-[2px]" data-id="element-222" data-mp="2c5a5p4dkzquc">
          {(['timeline', 'versions'] as const).map(t => <button key={t} role="tab" type="button" aria-selected={tab === t} onClick={() => setTab(t)} className={`h-[25px] rounded-[4px] text-[12px] transition-colors duration-150 ${tab === t ? 'bg-pf-blue text-white' : 'text-pf-muted hover:bg-pf-panel'}`} data-id="element-223" data-mp="1ohk4jqxek5kx">
            
              {t === 'timeline' ? 'Timeline' : `Saved Versions${versions.length ? ` (${versions.length})` : ''}`}
            </button>)}
        </div>
      </div>
      <div className="mt-[8px] border-b border-pf-line" data-id="element-224" data-mp="3ithdxba1haou" />

      <div className="min-h-0 flex-1 overflow-y-auto px-[10px] pb-[10px]" data-id="element-225" data-mp="3iv27axkmanl4">
        {tab === 'timeline' ? entries.length === 0 ? <div className="flex flex-col items-center px-6 pt-10 text-center" data-id="element-226" data-mp="3a04c1jtzyyqm">
              <HistoryIcon className="h-[20px] w-[20px] text-pf-faint" strokeWidth={1.8} data-id="element-227" data-mp="351jc7linqh2p" />
              <p className="mt-2 text-[12.5px] font-semibold text-pf-ink" data-id="element-228" data-mp="34zq3m0ohhms2">No changes yet</p>
              <p className="mt-1 text-[11.5px] leading-snug text-pf-muted" data-id="element-229" data-mp="304kllcy7cs99">Orienting, supporting, protecting and moving the model will appear here.</p>
            </div> : groups.map(g => <section key={g.day} aria-label={g.day} data-id="element-230" data-mp="z2bjf28is66s">
                <h3 className="pb-[4px] pt-[10px] text-[12.5px] font-bold text-pf-ink" data-id="element-231" data-mp="13zl98iztmjv5">{g.day}</h3>
                <ul data-id="element-232" data-mp="pb723vvdg98s">
                  <AnimatePresence initial={false} data-id="element-233" data-mp="p9w1g4x83hlw">
                    {g.items.map((e, i) => <HistoryEntryRow key={e.id} entry={e} today={g.isToday} selected={selected?.id === e.id} current={e.id === currentId} isFirst={i === 0} isLast={i === g.items.length - 1} onSelect={() => setSelectedId(e.id)} onRestore={() => onRequestRestore(e)} onSaveVersion={() => onSaveVersion(e)} data-id="element-234" data-mp="u87bogrfju4n" data-mpi={i} />)}
                  </AnimatePresence>
                </ul>
              </section>) : <SavedVersionsList versions={versions} onRestore={onRestoreVersion} data-id="element-235" data-mp="25p7194me7uuz" />}
      </div>

      {tab === 'timeline' && <div className="flex items-center gap-[8px] border-t border-pf-line px-[12px] py-[9px]" data-id="element-236" data-mp="3q7hmr3vd1wzu">
          <PillButton variant="outlineAccent" className="h-[28px] text-[12.5px] disabled:border-pf-line disabled:text-pf-faint disabled:hover:bg-white" disabled={!selected || selected.id === currentId} onClick={() => selected && onRequestRestore(selected)} data-id="element-237" data-mp="3l8xwir9qhz1o">
          
            {selected && selected.id === currentId ? 'This is the current version' : 'Restore to this version'}
          </PillButton>
          <MenuButton label="More version actions" placement="up" className="h-[28px] w-[30px] rounded-[7px] border border-pf-line" icon={<MoreHorizontalIcon className="h-[15px] w-[15px]" strokeWidth={2.4} data-id="element-239" data-mp="hrfpun5p6y2u" />} items={[{
        label: 'Save selected as version',
        onSelect: () => selected && onSaveVersion(selected),
        disabled: !selected
      }, {
        label: 'View saved versions',
        onSelect: () => setTab('versions')
      }]} data-id="element-238" data-mp="8sknjm5ry84t" />
        
        </div>}
    </div>;
}